// The shuttle between CAERA and GAMMIS.
//
// Until now a file left the building by hand: generate, download, log into the
// portal, upload, wait, download the acknowledgement, drop it back in. Every
// step is a place a week of billing can stop without anyone noticing, because
// nothing in the system knows the difference between "not sent yet" and
// "forgotten".
//
// This module is the part of that shuttle that has no runtime: the rules
// Gainwell imposes on file names, how to recognise what a downloaded file is,
// which submission an acknowledgement answers, and when the credentials are
// about to expire. The SSH and PGP plumbing lives in the worker, which is
// Node, because Deno has no usable SSH implementation. Keeping the rules here
// means they are covered by the same Jest suite as the rest of `edi/`, and the
// worker stays thin enough to read in one sitting.
//
// Everything here is taken from the SFTP Setup and Data Transfer Requirements
// Guide v5.4, §2.4 (naming), §1.3 (passwords), §1.4 (encryption) and the
// HIPAA file-name appendix on pages 13-15.

/** Where the files go. Port 22, SSH. */
import { filesWithGammis, destinationForPayer } from '../serviceModel.js';

export const GAMMIS_SFTP_HOST = 'sftp.mmis.georgia.gov';
export const GAMMIS_SFTP_PORT = 22;

/** §3.2. Far above a week of claims, which is about 60KB. */
export const SIZE_LIMIT_BYTES = 50 * 1024 * 1024;

/**
 * §2.4.2. The only extensions Gainwell allows a period before. Anything else
 * containing a period is rejected before it is read, which is the kind of
 * failure that looks like silence.
 */
export const ALLOWED_EXTENSIONS = ['txt', 'dat', 'zip', 'gz', 'pgp', 'asc'];

/**
 * The four naming rules, as problems rather than a boolean, because a name
 * that will be silently dropped deserves to be told why.
 *
 * Rule 4 is the surprising one: dashes and underscores may not be mixed. Ours
 * are all dashes (`837P-20261004-20261010-PROD.dat`), which also keeps the
 * name readable in a downloads folder.
 */
export const fileNameProblems = (name) => {
  const n = String(name || '');
  const out = [];

  if (!n) return ['The file has no name.'];
  if (/\s/.test(n)) out.push('File names may not contain spaces.');
  if (/[!#~@]/.test(n)) out.push('File names may not contain !, #, ~ or @.');
  if (n.includes('-') && n.includes('_')) {
    out.push('File names may use dashes or underscores, but not both.');
  }

  // Periods are allowed only immediately before an allowed extension, and the
  // chain `.dat.pgp` is explicitly sanctioned — Gainwell's own example is
  // CANDY_FOR_SALE.TXT.PGP.
  const parts = n.split('.');
  const stem = parts.shift();
  if (!stem) out.push('The file name has nothing before its first period.');
  for (const ext of parts) {
    if (!ALLOWED_EXTENSIONS.includes(ext.toLowerCase())) {
      out.push(`A period may only appear before ${ALLOWED_EXTENSIONS.join(', ')} — not before "${ext}".`);
    }
  }

  return out;
};

/**
 * §2.4.1. Upload under a temporary extension and rename when the bytes have
 * landed. The rename is the signal: it is what tells GAMMIS the transfer
 * finished, and it is why a connection dropped mid-upload leaves a `.tmp` file
 * that is ignored rather than half a batch of claims that is processed.
 */
export const uploadName = (finalName) => `${finalName}.tmp`;

/** The name a file takes once it is encrypted. */
export const encryptedName = (name) => `${name}.pgp`;

/** Join a remote directory and a file name without letting either escape. */
export const remotePath = (dir, name) => {
  const d = String(dir || '.').replace(/\/+$/, '');
  const n = String(name || '');
  if (n.includes('/') || n.includes('\\') || n.split('.').includes('..')) {
    throw new Error(`Refusing to build a remote path from "${n}".`);
  }
  return `${d}/${n}`;
};

// ── Recognising what came back ──────────────────────────────────────────────

/**
 * Gainwell names the files it sends us (appendix, pages 13-15), and the names
 * carry the document type as a token:
 *
 *   TA1  8310973_123456789_8310971_TA1X12BATCH_..._ORIGINAL_FILE.edi.TA1.pgp
 *   999  8064580_123456789_8058748_837PX12BATCH_..._FILE.edi.pgp.1.997.dat
 *   271  7681010_123456789_7679954_271X12BATCH_..._ORIGINAL270FILENAME.dat.pgp
 *   277  7862658_123456789_7861043_277X12BATCH_..._ORIGINAL276FILENAME.dat.pgp
 *   835  10302_835X12BATCH_ERA835_123456789A_001_20100203.out.dat.pgp
 *   277U 3368547_ERA277_234567890.dat.dat.pgp
 *
 * Note what the 999 example shows: the document type token names the file
 * being *answered* (837P), not the answer. So the extension chain has to be
 * read before the batch token, or every acknowledgement of an 837 would be
 * filed as an 837.
 */
export const classifyInbound = (fileName) => {
  const upper = String(fileName || '').toUpperCase();

  // A token bounded by separators. `\b` will not do: underscore is a word
  // character to a regular expression but a field separator to Gainwell, so
  // `\b835X12BATCH\b` never matches `_835X12BATCH_`.
  const has = (token) =>
    new RegExp(`(?:^|[^A-Z0-9])(?:${token})(?:[^A-Z0-9]|$)`).test(upper);

  // Extensions first — they describe the file itself. The 999 example in the
  // appendix is named for the 837P it answers, so reading the document-type
  // token first would file every acknowledgement as the thing acknowledged.
  if (has('TA1')) return 'TA1';
  if (has('99[79]')) return '999';

  // Then the document-type token.
  if (has('ERA835|835X12BATCH')) return '835';
  if (has('277U|ERA277')) return '277U';
  if (has('824X12BATCH')) return '824';
  if (has('271X12BATCH')) return '271';
  if (has('277X12BATCH')) return '277';

  return 'unknown';
};

/** Whether a listed file is still being written by the other end. */
export const isPartial = (fileName) => /\.tmp$/i.test(String(fileName || ''));

/** Whether a file needs decrypting before it can be read. */
export const isEncrypted = (fileName) => /\.(pgp|gpg|asc)$/i.test(String(fileName || ''));

/**
 * Which submission an acknowledgement answers.
 *
 * Gainwell echoes the original file name inside the name of its reply, which
 * is the whole point of the rule that a name must keep a static component
 * from submission to submission (§2.4.2). Matching on the name is exact where
 * parsing the content is not: a 999 names the functional group it answers, not
 * the file, and two files sent the same day can share a group control number.
 *
 * The stem is matched rather than the whole name because Gainwell appends its
 * own extension chain to ours.
 */
export const matchSubmissionByName = (inboundName, submissions = []) => {
  const n = String(inboundName || '').toUpperCase();
  let best = null;
  for (const sub of submissions) {
    const stem = String(sub?.file_name || '').split('.')[0].toUpperCase();
    if (!stem || !n.includes(stem)) continue;
    if (!best || stem.length > String(best.file_name || '').split('.')[0].length) best = sub;
  }
  return best;
};

// ── Credentials that expire quietly ─────────────────────────────────────────

/** §1.3. */
export const PASSWORD_LIFETIME_DAYS = 365;
/** §1.4, "revoke your public key and replace it every 24 months". */
export const PGP_KEY_LIFETIME_DAYS = 730;

const daysBetween = (from, to) =>
  Math.floor((Date.parse(to) - Date.parse(from)) / 86400000);

/**
 * The SFTP password expires after a year and the seven-day warning is given
 * "during sign on" — an interactive sign-on, which an automated client never
 * performs. So the warning would arrive nowhere, and the first sign of trouble
 * would be a week of claims that silently failed to upload. Tracking the date
 * ourselves is the only way this is ever seen coming.
 *
 * Dates are the two `company_info` rows, set when the credential is created.
 * An unset date is itself a warning: it means nobody recorded it.
 */
export const credentialWarnings = ({
  passwordSetOn = null,
  pgpKeyCreatedOn = null,
  today = new Date().toISOString().slice(0, 10),
} = {}) => {
  const out = [];

  const check = (setOn, lifetime, what, action) => {
    if (!setOn) {
      out.push({
        severity: 'info', what,
        message: `No date recorded for the ${what}, so its expiry cannot be tracked. Set it on Settings → General Info.`,
      });
      return;
    }
    const age = daysBetween(setOn, today);
    const left = lifetime - age;
    if (left <= 0) {
      out.push({ severity: 'expired', what, daysLeft: left,
        message: `The ${what} expired ${Math.abs(left)} day${Math.abs(left) === 1 ? '' : 's'} ago. ${action}` });
    } else if (left <= 7) {
      out.push({ severity: 'urgent', what, daysLeft: left,
        message: `The ${what} expires in ${left} day${left === 1 ? '' : 's'}. ${action}` });
    } else if (left <= 30) {
      out.push({ severity: 'warning', what, daysLeft: left,
        message: `The ${what} expires in ${left} days. ${action}` });
    }
  };

  check(passwordSetOn, PASSWORD_LIFETIME_DAYS, 'SFTP password',
    'Sign in to sftp.mmis.georgia.gov by hand to change it, then record the new date — an automated client is never shown the warning.');
  check(pgpKeyCreatedOn, PGP_KEY_LIFETIME_DAYS, 'PGP key pair',
    'Generate a replacement, send the new public key to the Gainwell EDI team, and record the date.');

  return out;
};

// ── What the worker is allowed to do ────────────────────────────────────────

/**
 * A downloaded file is routed by what it contains, not by what it is called.
 * The name is Gainwell's, and a convention documented in 2010 is a hint, not a
 * guarantee; the interchange inside says what it actually is. Where the two
 * disagree the content wins and the disagreement is recorded, because a naming
 * convention that has drifted is worth knowing about before it is worth
 * arguing with.
 */
export const reconcileKind = ({ fromName, fromContent }) => {
  if (!fromContent || fromContent === 'unknown') {
    return { kind: fromName, note: null };
  }
  if (!fromName || fromName === 'unknown' || fromName === fromContent) {
    return { kind: fromContent, note: null };
  }
  return {
    kind: fromContent,
    note: `The file name says ${fromName} but the interchange is a ${fromContent}. Routed as ${fromContent}.`,
  };
};

/** Where each kind of inbound file lands. */
export const INBOUND_DESTINATION = {
  TA1: 'claim_submissions',
  '824': 'claim_submissions',
  '277U': 'claim_submissions',
  '999': 'eligibility_checks',
  '271': 'eligibility_checks',
  '277': 'claim_lines',
  '835': 'remittances',
  unknown: null,
};

// ── Last check before a file leaves ─────────────────────────────────────────
//
// The 837P button refuses a run that does not file with GAMMIS and runs
// pre-flight before it builds a file. But the worker sends whatever submission
// is waiting, and nothing stopped a submission made some other way, or a run
// whose payer changed after its file was built, from reaching Gainwell. A
// CareSource child's claim in a GAMMIS file is a claim filed with the wrong
// payer. So the worker asks this, with the run and the lines the file carries,
// immediately before it uploads. (Coherence review, 2026-10-19.)
//
// Returns the reasons not to send; an empty list means send.

/**
 * @param {{ submission?: any, run?: any, lines?: any[] }} args
 * @returns {string[]}
 */
export const sendProblems = ({ submission, run, lines = [] } = {}) => {
  const problems = [];
  if (!submission) return ['No submission supplied.'];
  if (!run) return [`Submission ${submission.id} belongs to no billing run.`];
  if (!filesWithGammis(run)) {
    problems.push(`Its run files with ${run.payer}, not GAMMIS.`);
  }
  if (!lines.length) {
    problems.push('No claim line is attached to this file, so what it bills cannot be checked.');
  }
  const elsewhere = lines.filter(l => l.payer && destinationForPayer(l.payer) !== 'gammis');
  if (elsewhere.length) {
    const who = [...new Set(elsewhere.map(l => l.client_name || l.client_id))].slice(0, 5).join(', ');
    problems.push(`${elsewhere.length} line(s) belong to a payer that does not file with GAMMIS (${who}).`);
  }
  const foreign = lines.filter(l => l.billing_run_id && l.billing_run_id !== run.id);
  if (foreign.length) {
    problems.push(`${foreign.length} line(s) on this file belong to a different run.`);
  }
  return problems;
};
